import { Doc, generateToDocFn } from '@dadohr/shared/domainTypes/document';
import {
  LessonlyIntegrationConfig,
  LessonlyLesson,
  LessonlyPath
} from '@dadohr/shared/domainTypes/integrations/lessonly';
import { DEMO_ORG_IDS } from '@dadohr/shared/domainTypes/org';
import { CF, FS } from '@dadohr/shared/versions';
import shortid from 'shortid';
import { getAppUrl } from '../../../../../../admin/services/routes';
import {
  store,
  useMappedLoadingValue
} from '../../../../../../services/firebase';
import { callFirebaseFunction } from '../../../../../../services/firebaseFunctions';
import {
  createDocumentListenerGetter,
  useDocumentListener
} from '../../../../../../services/firecache/documentListener';
import { now } from '../../../../../../services/time';

export const toLessonlyConfigDoc = generateToDocFn<LessonlyIntegrationConfig>();

const COLLECTION = FS.lessonlyIntegrationConfig;
const collection = () => store().collection(COLLECTION);
export const lessonlyConfigStore = createDocumentListenerGetter(
  (orgId) => collection().doc(orgId),
  toLessonlyConfigDoc
);

export const useLessonlyConfig = (orgId: string, userId: string) => {
  return useMappedLoadingValue(
    useDocumentListener(lessonlyConfigStore(orgId)),
    (d) => {
      if (!d) {
        const n = now();
        const newConfig: Doc<LessonlyIntegrationConfig> = {
          id: orgId,
          collection: COLLECTION,
          data: {
            orgId,
            active: false,
            createdAt: n,
            createdBy: userId,
            editedAt: n,
            editedBy: userId,

            subdomain: '',
            apiKey: '',
            webhookActive: false,
            webhookAuthToken: `${shortid()}${shortid()}`
          }
        };
        return newConfig;
      }
      return d;
    }
  );
};

export const removeLessonlyConfig = (orgId: string) => {
  return collection().doc(orgId).delete();
};

export const getLessonlyWebhookUrl = () => `${getAppUrl()}api/lessonly/webhook`;

export const getLessonlyContentOptions = async (
  orgId: string
): Promise<(LessonlyLesson | LessonlyPath)[]> => {
  if (DEMO_ORG_IDS.includes(orgId)) {
    return [
      {
        resource_type: 'lesson',
        id: 123,
        title: 'Security Training',
        archived_at: null,
        archived_by_user_id: null,
        retake_score: 0,
        public: true,
        created_at: new Date().toISOString(),
        last_updated_at: new Date().toISOString(),
        tags: [
          {
            id: 1,
            name: 'Compliance',
            resource_type: 'tag'
          }
        ],
        links: { shareable: '', overview: '' }
      },

      {
        resource_type: 'lesson',
        id: 123,
        title: 'Privacy Training',
        archived_at: null,
        archived_by_user_id: null,
        retake_score: 0,
        public: true,
        created_at: new Date().toISOString(),
        last_updated_at: new Date().toISOString(),
        tags: [
          {
            id: 1,
            name: 'Compliance',
            resource_type: 'tag'
          }
        ],
        links: { shareable: '', overview: '' }
      },
      {
        resource_type: 'path',
        id: 123,
        title: `Use cases of our product`,
        archived_at: null,
        archived_by_user_id: null,
        public: true,
        created_at: new Date().toISOString(),
        last_updated_at: new Date().toISOString(),
        tags: [
          {
            id: 1,
            name: 'Compliance',
            resource_type: 'tag'
          }
        ],
        published_at: new Date().toISOString(),
        publisher_id: 12323,
        links: { shareable: '', overview: '' },
        is_certification: false
      }
    ];
  }
  return callFirebaseFunction<(LessonlyLesson | LessonlyPath)[]>(
    CF.lessonly.getContentOptions,
    { orgId }
  );
};
